import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import ConnectWithoutContactOutlinedIcon from "@mui/icons-material/ConnectWithoutContactOutlined";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import NotificationAddOutlinedIcon from "@mui/icons-material/NotificationAddOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";

import { useAppDispatch, useAppSelector } from "app/hooks";
import useMakeCall from "components/suno-dialer/call-patient/use-make-call.hook";
import { PAYMENTS_REQUESTS_FEATURE } from "constants/feature-flags";
import { selectCurrentClinic } from "features/auth/auth-slice";
import { hasClinicPaymentProvider } from "features/billing/components/sale-row-action.utils";
import { useDialstackSoftphone } from "features/calls/dialstack/softphone/use-dialstack-softphone.hook";
import { usePatientNotesDrawer } from "features/patients/patient-notes-drawer";
import { focusComposerState } from "features/patients/sections/patient-messages/messages/focus-composer-request";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import useEmail from "hooks/use-email.hook";
import useNewTouchpoint from "hooks/use-new-touchpoint";

import { requestNewAlert, requestNewAppointment } from "./quick-launcher.slice";
import type { QuickLauncherAction } from "./quick-launcher.types";
import type { QuickLauncherContext } from "./use-quick-launcher-context.hook";
import useQuickLauncherPatientTabActions from "./use-quick-launcher-patient-tab-actions.hook";

/**
 * Entries that exist only because of where the user is: the "This patient"
 * section on any chart route, plus "This invoice" on any sale route.
 *
 * Everything here is reachable from the app shell without the chart's own
 * providers — a URL, one of the drawer/dialog providers that `routes.tsx`
 * mounts *above* `Layout` (touchpoints, email, notes, tasks), or one of the
 * launcher's own hosted dialogs (`quick-launcher-hosts.component.tsx`: the
 * appointment form and the alert dialog). The softphone provider is below
 * `Layout`, which is why `QuickLauncher` is mounted inside it rather than
 * beside the side menu.
 *
 * Contact actions (touchpoint, email, call) wait for the narrow patient fetch:
 * the forms they open take a `Patient` object, not an id.
 */
export default function useQuickLauncherContextActions(
  context: QuickLauncherContext | null
): QuickLauncherAction[] {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const flags = useFlags();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const { handleOpen: openTouchpoint } = useNewTouchpoint();
  const { handleOpen: openEmail } = useEmail();
  const { handleOpenCreate: openCreateNote } = usePatientNotesDrawer();
  const { setPatientId: setTaskPatientId, handleOpenTaskModal } =
    useStaffTasksV2();
  const makeCall = useMakeCall();
  const { enabled: isSoftphoneEnabled } = useDialstackSoftphone();
  const tabActions = useQuickLauncherPatientTabActions(context);

  const canRequestOnlinePayment = Boolean(
    flags[PAYMENTS_REQUESTS_FEATURE] &&
      currentClinic &&
      hasClinicPaymentProvider(currentClinic)
  );

  return useMemo(() => {
    if (!context) return [];
    const { patientId, saleId, patient, patientLabel, isOnInvoicePage } =
      context;
    const chart = `/patients/${patientId}`;
    const actions: QuickLauncherAction[] = [];

    if (saleId !== undefined) {
      const sale = `${chart}/billing/${saleId}`;
      // Every sale form reads `originPathname` for its back/cancel navigation;
      // mirror `sale-row-action.component.tsx` so cancelling returns here.
      const state = { originPathname: pathname };
      const push = (
        action: Omit<QuickLauncherAction, "group" | "perform"> & {
          to: string;
        }
      ) => {
        const { to, ...rest } = action;
        actions.push({
          ...rest,
          group: "This invoice",
          perform: () => navigate(to, { state })
        });
      };

      push({
        id: "sale:record-payment",
        label: "Record payment",
        description: `Take a payment against invoice #${saleId}`,
        Icon: PaymentsOutlinedIcon,
        keywords: ["pay", "cash", "card", "collect"],
        to: `${sale}/payment`
      });
      if (canRequestOnlinePayment) {
        push({
          id: "sale:payment-request",
          label: "Send online payment request",
          description: `Email or text a pay-online link for invoice #${saleId}`,
          Icon: LinkOutlinedIcon,
          keywords: ["request", "link", "online", "pay"],
          to: `${sale}/payment-request`
        });
      }
      push({
        id: "sale:refund",
        label: "Issue refund",
        description: `Refund a payment on invoice #${saleId}`,
        Icon: CurrencyExchangeOutlinedIcon,
        keywords: ["return", "money back", "reverse"],
        // The legacy resolver route picks the patient/insurance form for us.
        to: `${sale}/refund/new`
      });
      push({
        id: "sale:adjustment",
        label: "Patient adjustment",
        description: `Write off or adjust invoice #${saleId}`,
        Icon: TuneOutlinedIcon,
        keywords: ["write off", "discount", "adjust"],
        to: `${sale}/adjustment`
      });
      if (!isOnInvoicePage) {
        push({
          id: "sale:view-invoice",
          label: "View invoice",
          description: `Open the printable invoice #${saleId}`,
          Icon: ReceiptOutlinedIcon,
          keywords: ["print", "pdf", "receipt"],
          to: `${sale}/invoice`
        });
      }
    }

    const firstName = patient?.preferred_name || patient?.first_name;
    const forWhom = firstName ? ` for ${firstName}` : "";

    actions.push({
      id: "patient:new-appointment",
      label: `New appointment${forWhom}`,
      description: patient
        ? `Open the appointment form with ${patientLabel} selected`
        : "Open the appointment form",
      group: "This patient",
      Icon: EventAvailableOutlinedIcon,
      keywords: ["book", "schedule", "visit", "calendar"],
      // Opens in place, on whatever page this is — the launcher hosts its own
      // appointment form. Before the patient fetch lands the form opens blank
      // rather than with a bare `{ id }` the patient picker cannot render.
      perform: () => dispatch(requestNewAppointment({ patient }))
    });

    actions.push({
      id: "patient:text",
      label: `Text${firstName ? ` ${firstName}` : ""}`,
      description: "Open the Messages tab with the composer focused",
      group: "This patient",
      Icon: ChatBubbleOutlineOutlinedIcon,
      keywords: ["sms", "message", "inbox", "conversation"],
      perform: () =>
        navigate(`${chart}/touchpoints/messages`, {
          state: focusComposerState()
        })
    });

    if (patient) {
      actions.push({
        id: "patient:new-touchpoint",
        label: "New touchpoint",
        description: `Log or schedule a touchpoint for ${patientLabel}`,
        group: "This patient",
        Icon: ConnectWithoutContactOutlinedIcon,
        keywords: ["follow up", "outreach", "contact", "reminder"],
        perform: () => openTouchpoint(patient)
      });
      actions.push({
        id: "patient:send-email",
        label: `Email ${firstName}`,
        description: patient.email
          ? `Compose to ${patient.email}`
          : "Compose an email — no address on file",
        group: "This patient",
        Icon: EmailOutlinedIcon,
        keywords: ["mail", "compose", "send"],
        perform: () => openEmail({ patient })
      });
      if (isSoftphoneEnabled && patient.phone) {
        actions.push({
          id: "patient:call",
          label: `Call ${firstName}`,
          description: `Dial ${patient.phone} from the softphone`,
          group: "This patient",
          Icon: CallOutlinedIcon,
          keywords: ["phone", "dial", "ring"],
          perform: () => makeCall(patient.phone as string, patient)
        });
      }
    }

    actions.push(
      {
        id: "patient:new-note",
        label: "New note",
        description: `Add a note to ${patientLabel}'s chart`,
        group: "This patient",
        Icon: NoteAddOutlinedIcon,
        keywords: ["chart note", "notes", "chart"],
        perform: () => openCreateNote(patientId)
      },
      {
        id: "patient:new-task",
        label: "New task",
        description: `Create a task about ${patientLabel}`,
        group: "This patient",
        Icon: TaskAltOutlinedIcon,
        keywords: ["todo", "follow up", "assign"],
        perform: () => {
          setTaskPatientId(patientId);
          handleOpenTaskModal({
            mode: "create",
            ignoreTaskPreviewAfterCreation: true
          });
        }
      },
      {
        id: "patient:new-alert",
        label: "New alert",
        description: `Add an alert to ${patientLabel}'s chart`,
        group: "This patient",
        Icon: NotificationAddOutlinedIcon,
        keywords: ["warning", "flag", "notice", "banner"],
        perform: () =>
          dispatch(
            requestNewAlert({
              patientId,
              // Mirrors the header's own `patientName` prop (first + last), so
              // the dialog title reads the same from both entry points.
              patientName: patient
                ? `${patient.first_name} ${patient.last_name}`.trim()
                : undefined
            })
          )
      },
      {
        id: "patient:new-invoice",
        label: "New invoice",
        description: `Start a sale for ${patientLabel}`,
        group: "This patient",
        Icon: ReceiptLongOutlinedIcon,
        keywords: ["sale", "bill", "charge", "quote"],
        perform: () => navigate(`${chart}/billing/new`)
      },
      ...tabActions
    );

    return actions;
  }, [
    context,
    pathname,
    dispatch,
    navigate,
    canRequestOnlinePayment,
    isSoftphoneEnabled,
    openTouchpoint,
    openEmail,
    openCreateNote,
    setTaskPatientId,
    handleOpenTaskModal,
    makeCall,
    tabActions
  ]);
}
